<template>
  <div>
    <upgrade-subscription-modal
      :is-modal-active="isSubscriptionModalActive"
      :workspace-user-count="Object.keys(inviteTeammatesForm).length + 1"
      @close="handleSubscriptionModalClose"
    />
    <h5 class="title is-5">
      Invite your team members
    </h5>
    <p class="is-size-6 u-m-b">
      Who else is working in this team with you?
    </p>
    <form
      id="invite-team-members"
      @submit.prevent="handleInviteTeammatesSubmit"
    >
      <b-field
        v-for="teammateKey in Object.keys(inviteTeammatesForm)"
        grouped
        :key="teammateKey"
      >
        <b-field
          :label="inviteTeammatesForm[teammateKey].label"
          :type="formErrors[inviteTeammatesForm[teammateKey].name] ? 'is-danger' : ''"
          :message="formErrors[inviteTeammatesForm[teammateKey].name] || ''"
          expanded
        >
          <b-input
            v-model="inviteTeammatesForm[teammateKey].value"
            :name="inviteTeammatesForm[teammateKey].name"
            placeholder="george@vandelay.com"
            :ref="`teammateEmailInput${teammateKey}`"
            :disabled="isFormSubmissionInProgress"
          />
        </b-field>
        <b-field class="team-member-input__close-button__field">
          <p class="control">
            <b-icon
              v-if="Object.keys(inviteTeammatesForm).length > 1"
              icon="close"
              type="is-danger"
              @click.native="handleRemoveTeammate(teammateKey)"
            />
          </p>
        </b-field>
      </b-field>
      <div class="u-m-b">
        <b-button
          v-if="doesWorkspaceRequireUpgrade"
          type="is-dark"
          outlined
          size="is-small"
          @click.prevent="handleAddAnotherTeammateField"
          icon-left="plus"
          :disabled="isFormSubmissionInProgress"
        >
          Add another
        </b-button>
        <div v-else>
          <p class="is-size-7 u-m-b-small">
            To invite more team members, upgrade to our
            <span class="has-text-weight-bold">Growth Plan</span>
            (for teams of 6 or more people)
          </p>
          <b-button
            type="is-success"
            size="is-small"
            @click="handleOpenSubscriptionModal"
            @keyup.enter="handleOpenSubscriptionModal"
          >
            Upgrade To Growth Plan
          </b-button>
        </div>
      </div>
      <b-button
        native-type="submit"
        type="is-primary"
        size="is-large"
        class="is-fullwidth"
        :loading="isFormSubmissionInProgress"
      >
        Invite
        {{ Object.keys(inviteTeammatesForm).length }}
        Team Member{{ Object.keys(inviteTeammatesForm).length > 1 ? 's' : '' }}
      </b-button>
      <div class="has-text-right u-m-t-xsmall">
        <b-button
          type="is-text"
          size="is-small"
          class="is-paddingless"
          @click.prevent="handleSkipThisStep()"
        >
          I'll do this later
        </b-button>
      </div>
    </form>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import * as ValidationService from '@/services/Validation.service';
import * as InvitationActions from '@/store/actions/Invitation.actions';
import { isObjectEmpty } from '@/helpers/dataHelpers';
import * as InvitationConstants from '@/constants/Invitation.constants';
import * as SubscriptionTypes from '@/constants/Subscription.types';
import UpgradeSubscriptionModal from '@/components/UpgradeSubscriptionModal.vue';

export default {
  name: 'OnboardingInviteTeamMembersForm',

  components: {
    UpgradeSubscriptionModal,
  },

  props: {
    activeTeamId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      SubscriptionTypes,
      inviteTeammatesForm: {
        teammate1: {
          name: 'Teammate 1 Email',
          label: 'Teammate 1 Email',
          value: '',
          required: true,
          validator: ValidationService.validateEmail,
        },
        teammate2: {
          name: 'Teammate 2 Email',
          label: 'Teammate 2 Email',
          value: '',
          required: true,
          validator: ValidationService.validateEmail,
        },
      },
      isFormSubmissionInProgress: false,
      formErrors: {},
      isSubscriptionModalActive: false,
    };
  },

  computed: {
    ...mapState({
      activeWorkspaceId: state => state.workspace.activeWorkspaceId,
      teams: state => state.team.teams,
    }),
    ...mapGetters([
      'activeWorkspace',
      'loggedInUser',
      'activeWorkspaceSubscription',
    ]),
    doesWorkspaceRequireUpgrade() {
      return (
        Object.keys(this.inviteTeammatesForm).length < 4
        || this.activeWorkspaceSubscription.stripePricingPlan === SubscriptionTypes.STRIPE_PRICE_TYPE_GROWTH(this.$env)
      );
    },
    inviteTeammatesFormPayload() {
      return Object.keys(this.inviteTeammatesForm)
        .map(formFieldKey => ({
          inviteeEmail: this.inviteTeammatesForm[formFieldKey].value,
          workspaceId: this.activeWorkspaceId,
          workspaceName: this.activeWorkspace.workspaceName,
          statusId: InvitationConstants.invitationStatusIds.PENDING,
          inviterId: this.loggedInUser.id,
          inviterFirstName: this.loggedInUser.firstName,
          inviterLastName: this.loggedInUser.lastName,
          teamId: this.activeTeamId,
          teamName: this.teams[this.activeTeamId].teamName,
        }));
    },
  },

  mounted() {
    if (!this.activeTeamId) this.handleSkipThisStep();
    this.setInitialFormState();
  },

  methods: {
    ...mapActions({
      createInvitations: InvitationActions.CREATE_INVITATIONS,
    }),
    setInitialFormState() {
      this.$refs.teammateEmailInputteammate1[0].$el.querySelector('input').focus();
    },
    handleInviteTeammatesSubmit() {
      this.formErrors = ValidationService.runFormValidation({ ...this.inviteTeammatesForm });
      if (!isObjectEmpty(this.formErrors)) return;

      this.isFormSubmissionInProgress = true;
      this.createInvitations({ newInvitations: this.inviteTeammatesFormPayload })
        .then(() => {
          this.$emit('submitSuccess');
          this.isFormSubmissionInProgress = false;
        })
        .catch(() => {
          this.isFormSubmissionInProgress = false;
        });
    },
    handleAddAnotherTeammateField() {
      const teammateNumber = Object.keys(this.inviteTeammatesForm).length + 1;
      const newTeammember = {
        name: `Teammate ${teammateNumber} Email`,
        label: `Teammate ${teammateNumber} Email`,
        value: '',
        required: true,
        validator: ValidationService.validateEmail,
      };
      this.inviteTeammatesForm = { ...this.inviteTeammatesForm, [`teammate${teammateNumber}`]: newTeammember };
    },
    handleRemoveTeammate(key) {
      const newForm = { ...this.inviteTeammatesForm };
      delete newForm[key];
      this.inviteTeammatesForm = { ...newForm };
    },
    handleSkipThisStep() {
      this.$emit('skipStep');
    },
    handleSubscriptionModalClose() {
      this.isSubscriptionModalActive = false;
    },
    handleOpenSubscriptionModal() {
      this.isSubscriptionModalActive = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.field:not(:last-child) {
    margin-bottom: $global-whitespace-small;
}

.team-member-input__close-button__field {
  padding-top: 2.5rem;
  cursor: pointer;
}
</style>
